import { API_BASE_URL } from "@kupikupi/brand";
import { useQuery } from "@tanstack/react-query";
import {
  getBonusWalletInfo,
  getMonthlyRankInfo,
  getPromoteStatInfo,
  listCommissionConfig,
} from "../endpoints/promote";
import type {
  PromoteBonusWallet,
  PromoteCommissionConfig,
  PromoteMonthlyRank,
  PromoteStat,
} from "../types/promote";

const ctx = () => ({ baseUrl: API_BASE_URL });

/**
 * Tier ladder config — the single source of truth for commission rates and
 * order-user thresholds. Rarely changes; cache long. Public-readable so the
 * affiliate landing page shows rates to logged-out visitors (backend opened
 * /newPromoteAlliance/listCommissionConfig to anonymous, aps-boot#299): no
 * `enabled` gate, token forwarded only when present.
 */
export function usePromoteCommissionConfig(authToken: string | null) {
  return useQuery<PromoteCommissionConfig[]>({
    queryKey: ["promote", "commission-config"],
    queryFn: () => listCommissionConfig(ctx(), authToken),
    staleTime: 30 * 60 * 1000,
  });
}

/** Commission wallet (settled / unsettled / withdrawn / total). */
export function usePromoteBonusWallet(authToken: string | null) {
  return useQuery<PromoteBonusWallet>({
    queryKey: ["promote", "bonus-wallet"],
    enabled: !!authToken,
    queryFn: () => getBonusWalletInfo(ctx(), authToken as string),
    staleTime: 60 * 1000,
  });
}

/** Earnings-board stats (invited / order / sign users + commission totals). */
export function usePromoteStat(authToken: string | null) {
  return useQuery<PromoteStat>({
    queryKey: ["promote", "stat"],
    enabled: !!authToken,
    queryFn: () => getPromoteStatInfo(ctx(), authToken as string),
    staleTime: 60 * 1000,
  });
}

/**
 * This-month leaderboard. Public-readable top list (social proof on the landing
 * page, aps-boot#299): no `enabled` gate. The signed-in user's own row
 * (`currentUserRankInfo`) is only populated when a token is forwarded; for
 * anonymous visitors the backend returns it null and the Leaderboard hides the
 * "me" row.
 */
export function usePromoteMonthlyRank(authToken: string | null) {
  return useQuery<PromoteMonthlyRank>({
    queryKey: ["promote", "monthly-rank"],
    queryFn: () => getMonthlyRankInfo(ctx(), authToken),
    staleTime: 5 * 60 * 1000,
  });
}
